/* global Excel */

import type { ConnectedWorkbook } from "../chat/types";

const STORAGE_KEY = "excel-agent-connected-workbooks";
const HEARTBEAT_INTERVAL = 5000; // 5 seconds
const STALE_THRESHOLD = 15000; // 15 seconds

export const instanceId = crypto.randomUUID();

export function registerWorkbook(name: string) {
  const update = () => {
    const raw = localStorage.getItem(STORAGE_KEY);
    let workbooks: ConnectedWorkbook[] = [];
    try {
      if (raw) workbooks = JSON.parse(raw);
    } catch (e) {
      console.error("Failed to parse connected workbooks from localStorage", e);
    }

    const now = Date.now();
    // Prune stale entries
    workbooks = workbooks.filter((wb) => now - wb.activeAt < STALE_THRESHOLD);

    // Add or update current instance
    const existingIdx = workbooks.findIndex((wb) => wb.id === instanceId);
    if (existingIdx >= 0) {
      workbooks[existingIdx].activeAt = now;
      workbooks[existingIdx].name = name;
    } else {
      workbooks.push({ id: instanceId, name, activeAt: now });
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(workbooks));
  };

  update();
  const interval = setInterval(update, HEARTBEAT_INTERVAL);

  // Initial cleanup
  window.addEventListener("beforeunload", () => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        let workbooks: ConnectedWorkbook[] = JSON.parse(raw);
        workbooks = workbooks.filter((wb) => wb.id !== instanceId);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(workbooks));
      } catch (e) {}
    }
    clearInterval(interval);
  });

  return () => {
    clearInterval(interval);
  };
}

export function getConnectedWorkbooks(): ConnectedWorkbook[] {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    const now = Date.now();
    let workbooks: ConnectedWorkbook[] = JSON.parse(raw);
    // Filter out current instance from the list we show in the dropdown (or mark it)
    return workbooks
      .filter((wb) => now - wb.activeAt < STALE_THRESHOLD)
      .map((wb) => ({
        ...wb,
        isCurrent: wb.id === instanceId,
      })) as (ConnectedWorkbook & { isCurrent: boolean })[];
  } catch (e) {
    return [];
  }
}

export function onConnectedWorkbooksChange(callback: (workbooks: ConnectedWorkbook[]) => void) {
  const handler = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) {
      callback(getConnectedWorkbooks());
    }
  };
  window.addEventListener("storage", handler);
  
  // Also poll locally since storage event only fires for other windows
  const interval = setInterval(() => {
      callback(getConnectedWorkbooks());
  }, 2000);

  return () => {
    window.removeEventListener("storage", handler);
    clearInterval(interval);
  };
}
